import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  inject,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subscription, Observable } from "rxjs";
import { first } from "rxjs/operators";
import { UserModel } from "../../models/user.model";
import { AuthService } from "../../services/auth.service";
import { ActivatedRoute, Router } from "@angular/router";
import { SetError } from "src/utils/set-error.validator";
import { LoginDialogComponent } from "../dialogs/login-dialog/login-dialog.component";
import { MatDialogRef } from "@angular/material/dialog";
import { MenuComponent } from "src/app/_metronic/kt/components";
import { AuthState } from "../../models/auth.model";
import { DEMO } from "src/app/config/brand.config";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  readonly demo = DEMO;

  // KeenThemes mock, change it to:
  defaultAuth: any = {
    email: "admin@demo.com",
    password: "demo",
  };
  loginForm: FormGroup;
  hasError: boolean;
  /** Translation key for the failure the API actually reported. */
  errorKey = "AUTH.INVALID_LOGIN";
  error: any = {};
  returnUrl: string;
  isLoading$: Observable<boolean>;

  AuthStates = AuthState;

  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.isLoading$ = this.authService.isLoading$;
    // redirect to home if already logged in
    if (this.authService.currentUserValue) {
      this.router.navigate(["/"]);
    }
  }

  ngOnInit(): void {
    this.initForm();
    // get return url from route parameters or default to '/'
    this.returnUrl =
      this.route.snapshot.queryParams["returnUrl".toString()] || "/";
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  initForm() {
    this.loginForm = this.fb.group({
      email: [
        null,
        Validators.compose([
          Validators.required,
          Validators.email,
          Validators.minLength(3),
          Validators.maxLength(320), // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
        ]),
      ],
      password: [
        null,
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
    });
  }

  /**
   * "Invalid username and password" is only one of the ways signing in fails.
   * An account still awaiting approval, or one an admin rejected, used to get
   * the same wrong-password message and no way to work out what to do.
   */
  private messageFor(apiError: string | null): string {
    switch (apiError) {
      case "Please wait for approve.":
        return "AUTH.WAIT_APPROVE";
      case "This user is reject.":
        return "AUTH.ACCOUNT_REJECTED";
      default:
        return "AUTH.INVALID_LOGIN";
    }
  }

  submit() {
    this.hasError = false;
    this.errorKey = "AUTH.INVALID_LOGIN";
    this.error = {};
    if (this.loginForm.invalid) {
      this.error = SetError(this.loginForm);
      return;
    }
    const payload = this.loginForm.getRawValue();
    const loginSubscr = this.authService
      .login(payload)
      .pipe(first())
      .subscribe((user: UserModel | undefined) => {
        if (user) {
          // this.#dialogRef.close();
          window.location.reload();
          // this.router.navigate([this.returnUrl]);
        } else {
          this.errorKey = this.messageFor(this.authService.loginError());
          this.hasError = true;
        }
      });

    this.unsubscribe.push(loginSubscr);
  }

  /**
   * One-click entry for the public demo.
   *
   * Visitors should not have to guess a password to look around, and the
   * credentials are seeded and public anyway. Compiled out of customer builds
   * with the rest of DEMO.
   */
  loginAsDemo() {
    this.loginForm.patchValue({
      email: DEMO.account.username,
      password: DEMO.account.password,
    });
    this.submit();
  }

  /** The same door, into the parent's half of the demo. */
  loginAsDemoParent() {
    this.loginForm.patchValue({
      email: DEMO.parentAccount.username,
      password: DEMO.parentAccount.password,
    });
    this.submit();
  }

  updateAuthState(state: AuthState) {
    this.authService.updateAuthState(state);
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
